import { NavLink, useLocation, Link } from "react-router-dom";
import { useState } from "react";
import { LayoutDashboard, CalendarDays, Users, LogOut, BarChart2, CreditCard, FileSpreadsheet, MapPin, MoreHorizontal, Lock, WifiOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { OfflineBanner } from "@/components/OfflineBanner";
import { useOfflineSync } from "@/hooks/useOfflineSync";

const mainNavItems = [
  { to: "/app", icon: LayoutDashboard, label: "Dashboard", exact: true },
  { to: "/app/clienti", icon: Users, label: "Clienti" },
  { to: "/app/mappa", icon: MapPin, label: "Mappa" },
  { to: "/app/calendario", icon: CalendarDays, label: "Calendario" },
];

const premiumNavItems = [
  { to: "/app/statistiche", icon: BarChart2, label: "Statistiche" },
  { to: "/app/biglietti", icon: CreditCard, label: "Biglietti" },
  { to: "/app/import-excel", icon: FileSpreadsheet, label: "Import Excel" },
];

function getInitials(user) {
  if (!user) return "?";
  const name = user.name || user.email || "";
  return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "?";
}

export default function Layout({ children, fullHeight = false }) {
  const location = useLocation();
  const { user, logout, isPremium, subscription } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const { pending } = useOfflineSync();

  // Banner impersonificazione admin
  const adminKey = localStorage.getItem("ve_admin_key");
  const returnToAdmin = () => {
    localStorage.removeItem("ve_admin_key");
    localStorage.removeItem("ve_token");
    localStorage.removeItem("ve_user_cache");
    // ve_admin_saved_key rimane → auto-login al ritorno
    window.location.href = "/admin";
  };

  const isManager = user?.role === "manager";
  const effectiveNavItems = isManager
    ? [{ to: "/app/manager", icon: LayoutDashboard, label: "Dashboard", exact: true }]
    : mainNavItems;

  const isPremiumPath = premiumNavItems.some(item => location.pathname.startsWith(item.to));

  return (
    <div className={`flex min-h-screen bg-[#F8FAFC] ${adminKey ? "pt-10" : ""}`}>
      <OfflineBanner />

      {/* Banner impersonificazione admin */}
      {adminKey && (
        <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-white px-4 py-2 flex items-center justify-between text-sm font-medium shadow-lg">
          <span>Stai navigando come <strong>{user?.email || user?.name}</strong></span>
          <button
            onClick={returnToAdmin}
            className="flex items-center gap-1.5 bg-white text-amber-700 text-xs font-bold px-3 py-1 rounded-lg hover:bg-amber-50 transition-colors"
          >
            ← Torna all'admin
          </button>
        </div>
      )}
      {/* Sidebar Desktop */}
      <aside className="hidden lg:flex flex-col fixed left-0 top-0 h-full w-56 bg-[#0F172A] text-white z-30">
        <div className="flex items-center gap-2 px-5 py-5 border-b border-white/10">
          <img src="/icon-192.png" alt="venditoreElite" className="w-8 h-8 rounded-lg object-cover" />
          <div>
            <div className="font-heading font-bold text-base leading-tight">venditoreElite</div>
            <div className="text-[10px] text-white/50">Calendario Visite</div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {effectiveNavItems.map(({ to, icon: Icon, label, exact }) => {
            const isActive = exact ? location.pathname === to : location.pathname.startsWith(to);
            return (
              <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase()}`}
                className={cn("flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                  isActive ? "bg-white/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"
                )}>
                <Icon className="w-4 h-4 flex-shrink-0" />
                {label}
              </NavLink>
            );
          })}

          {/* Premium section */}
          <div className="pt-3">
            <div className="flex items-center gap-2 px-3 pb-1.5">
              <span className="text-[10px] uppercase tracking-widest text-white/30 font-semibold">Premium</span>
              {!isPremium && <span className="text-[9px] bg-violet-500/30 text-violet-300 px-1.5 py-0.5 rounded-full">€4,90/mese</span>}
            </div>
            {premiumNavItems.map(({ to, icon: Icon, label }) => {
              const isActive = location.pathname.startsWith(to);
              return (
                <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(" ", "-")}`}
                  className={cn("flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                    isActive ? "bg-white/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5",
                    !isPremium && "opacity-50"
                  )}>
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {label}
                  {!isPremium && <Lock className="w-3 h-3 ml-auto text-white/30" />}
                </NavLink>
              );
            })}
          </div>
        </nav>

        <div className="px-5 py-4 border-t border-white/10 space-y-2">
          {user && <p className="text-[11px] text-white/50 truncate">{user.name || user.email}</p>}
          {subscription !== "free" && <p className="text-[10px] text-violet-400 font-medium capitalize">{subscription}</p>}
          {pending > 0 && (
            <div
              data-testid="offline-pending-sidebar"
              className="flex items-center gap-1.5 bg-amber-500/20 text-amber-400 text-[10px] font-medium px-2 py-1 rounded-md"
            >
              <WifiOff className="w-3 h-3 flex-shrink-0" />
              {pending} in attesa di sync
            </div>
          )}
          <button onClick={logout} className="flex items-center gap-2 text-[11px] text-white/30 hover:text-white/70 transition-colors">
            <LogOut className="w-3 h-3" /> Esci
          </button>
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            <Link to="/privacy-policy" className="text-[10px] text-white/30 hover:text-white/60 transition-colors">Privacy</Link>
            <Link to="/cookie-policy" className="text-[10px] text-white/30 hover:text-white/60 transition-colors">Cookie</Link>
            <Link to="/contatti" className="text-[10px] text-white/30 hover:text-white/60 transition-colors">Contatti</Link>
          </div>
        </div>
      </aside>

      {/* ── MOBILE SHELL: flex colonna a tutta altezza ── */}
      <div className="mobile-app-shell flex flex-col w-full lg:hidden">
        {/* Mobile header */}
        <header className="flex-none bg-[#0F172A] text-white px-4 py-3 flex items-center justify-between gap-2 z-20">
          <div className="flex items-center gap-2">
            <img src="/icon-192.png" alt="venditoreElite" className="w-7 h-7 rounded-md object-cover" />
            <span className="font-heading font-bold text-base">venditoreElite</span>
          </div>
          <div className="relative">
            <button onClick={() => setShowUserMenu(v => !v)} data-testid="mobile-user-menu-btn"
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors rounded-full pl-2 pr-3 py-1.5">
              <div className="relative w-6 h-6 flex-shrink-0">
                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-[11px] font-bold">
                  {getInitials(user)}
                </div>
                {pending > 0 && (
                  <span
                    data-testid="offline-pending-badge"
                    className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center text-[9px] font-bold text-white"
                  >
                    {pending > 9 ? "9+" : pending}
                  </span>
                )}
              </div>
              <span className="text-xs font-medium text-white/80 max-w-[100px] truncate">
                {user?.name || user?.email?.split("@")[0] || "Utente"}
              </span>
            </button>
            {showUserMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-100 z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <p className="text-xs font-semibold text-[#0F172A] truncate">{user?.name || "Utente"}</p>
                    <p className="text-[11px] text-slate-400 truncate">{user?.email}</p>
                    {subscription !== "free" && <p className="text-[10px] text-violet-600 font-medium capitalize mt-0.5">{subscription}</p>}
                  </div>
                  <button onClick={() => { setShowUserMenu(false); logout(); }} data-testid="mobile-logout-btn"
                    className="flex items-center gap-2 w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors">
                    <LogOut className="w-4 h-4" />
                    Esci dall'account
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        {/* Contenuto scrollabile */}
        <main className={cn(
          "flex-1 min-h-0 flex flex-col",
          fullHeight ? "mobile-main-full" : "mobile-main-scroll bg-[#F8FAFC]"
        )}>
          {children}
        </main>

        {/* Bottom nav — in fondo al flex, sempre sopra la mappa */}
        <nav className="flex-none relative bg-white border-t border-slate-200 flex"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)', height: 'calc(56px + env(safe-area-inset-bottom, 0px))', zIndex: 10000 }}>
          {mainNavItems.map(({ to, icon: Icon, label, exact }) => {
            const isActive = exact ? location.pathname === to : location.pathname.startsWith(to);
            return (
              <NavLink key={to} to={to} data-testid={`mobile-nav-${label.toLowerCase()}`}
                className={cn("flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors",
                  isActive ? "text-[#0F172A]" : "text-slate-400"
                )}>
                <Icon className={cn("w-5 h-5", isActive && "stroke-[2.5]")} />
                {label}
              </NavLink>
            );
          })}
          <button
            data-testid="mobile-nav-altro"
            onClick={() => setShowMore(true)}
            className={cn("flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors",
              isPremiumPath ? "text-[#0F172A]" : "text-slate-400"
            )}
          >
            <MoreHorizontal className={cn("w-5 h-5", isPremiumPath && "stroke-[2.5]")} />
            Altro
          </button>
        </nav>
      </div>

      {/* ── DESKTOP: layout originale ── */}
      <main className="hidden lg:block flex-1 lg:ml-56 lg:pb-0">
        <div className={fullHeight ? "flex flex-col h-full" : "min-h-screen"}>
          {children}
        </div>
      </main>

      {/* More panel - Mobile */}
      {showMore && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowMore(false)} />
          <div
            className="fixed left-0 right-0 bg-white rounded-t-2xl flex flex-col"
            style={{
              zIndex: 20000,
              bottom: 'calc(56px + env(safe-area-inset-bottom, 0px))',
              maxHeight: 'calc(85dvh - 56px)',
            }}
          >
            <div className="flex-none px-5 pt-5">
              <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-5" />
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-[#0F172A]">Funzioni Premium</h3>
                {!isPremium && (
                  <NavLink to="/app/premium" onClick={() => setShowMore(false)}
                    className="text-xs bg-violet-100 text-violet-700 px-3 py-1 rounded-full font-semibold">
                    €4,90/mese
                  </NavLink>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 pb-6 space-y-1">
              {premiumNavItems.map(({ to, icon: Icon, label }) => {
                const isActive = location.pathname.startsWith(to);
                return (
                  <NavLink key={to} to={to} onClick={() => setShowMore(false)}
                    data-testid={`more-nav-${label.toLowerCase().replace(" ", "-")}`}
                    className={cn("flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all w-full",
                      isActive ? "bg-[#0F172A] text-white" : "text-[#0F172A] hover:bg-slate-50",
                      !isPremium && "opacity-50"
                    )}>
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {label}
                    {!isPremium && <Lock className="w-4 h-4 ml-auto text-slate-400" />}
                  </NavLink>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

